import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Select,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  ClearAll,
  Warning
  // Print
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

// Notification interface based on API response
interface Notification {
  NotificationID: number;
  EmailSubject: string;
  EmailID: string;
  SentOn: string;
  EmailStatus: string;
}

// API Response interface
interface NotificationAPIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    pageSize: number;
    totalPages: number;
    Data: Notification[];
  };
}

// Contract Numbers API Response interface
interface ContractNumbersAPIResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    data: string[];
  };
}

// Search criteria interface - Updated contractNo to be string instead of array
interface SearchCriteria {
  titleEmailSubject: string;
  contractNo: string; // Changed from contractNos: number[] to contractNo: string
  statuses: number[];
  types: number[];
  categories: number[];
  bidDeadline: Date | null;
  notificationStatus: string;
}

// Dropdown option interfaces
interface StatusOption {
  StatusID: number;
  Status: string;
  Active: boolean;
}

interface TypeOption {
  TypeID: number;
  Type: string;
  Active: boolean;
}

interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

const BidNotifications: React.FC = () => {
  const theme = useTheme();

  // Search criteria state - Updated initial state
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    titleEmailSubject: '',
    contractNo: '', // Changed from contractNos: [] to contractNo: ''
    statuses: [],
    types: [],
    categories: [],
    bidDeadline: null,
    notificationStatus: 'All'
  });

  // Main data states
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Dropdown data states - Added contractNumbers
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [contractNumbers, setContractNumbers] = useState<string[]>([]); // Added contract numbers state
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);
  const [openStatusSelect, setOpenStatusSelect] = useState(false);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);
  const [openContractSelect, setOpenContractSelect] = useState(false);

  // Fetch dropdown data on component mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      setDropdownError(null);
      try {
        // Fetch categories
        const categoriesResponse = await httpRequest(
          'GET',
          `/bid_portal/category/get_all`,
          {},
          {}
        );

        if (categoriesResponse.status === 'success') {
          const categoriesData =
            categoriesResponse.data?.data ||
            categoriesResponse.data?.Data ||
            [];
          setCategories(categoriesData);
        }

        // Fetch types
        const typesResponse = await httpRequest(
          'GET',
          `/bid_portal/type/get_all`,
          {},
          {}
        );

        if (typesResponse.status === 'success') {
          const typesData =
            typesResponse.data?.data || typesResponse.data?.Data || [];
          setTypes(typesData);
        }

        // Fetch statuses
        const statusesResponse = await httpRequest(
          'GET',
          `/bid_portal/status/get_all`,
          {},
          {}
        );

        if (statusesResponse.status === 'success') {
          const statusesData =
            statusesResponse.data?.data || statusesResponse.data?.Data || [];
          setStatuses(statusesData);
        }

        // Fetch contract numbers - New API call
        const contractNumbersResponse = (await httpRequest(
          'GET',
          '/bid_portal/bids/contractNo',
          {},
          {}
        )) as ContractNumbersAPIResponse;

        if (contractNumbersResponse.status === 'success') {
          const contractNumbersData = contractNumbersResponse.data?.data || [];
          setContractNumbers(contractNumbersData);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setDropdownError('Failed to load form options. Please try again.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch notifications
  const fetchNotifications = async (
    pageNum: number,
    pageSize: number,
    criteria?: SearchCriteria // Add optional parameter
  ) => {
    setLoading(true);
    try {
      // Use provided criteria or fall back to current state
      const activeCriteria = criteria || searchCriteria;

      // Construct query parameters using activeCriteria instead of searchCriteria
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString(),
        ...(activeCriteria.titleEmailSubject.trim() && {
          Title: activeCriteria.titleEmailSubject.trim()
        }),
        ...(activeCriteria.contractNo && {
          ContractNo: activeCriteria.contractNo
        }),
        ...(activeCriteria.statuses.length > 0 && {
          Status: activeCriteria.statuses.join(',')
        }),
        ...(activeCriteria.types.length > 0 && {
          Type: activeCriteria.types.join(',')
        }),
        ...(activeCriteria.categories.length > 0 && {
          Categories: activeCriteria.categories.join(',')
        }),
        ...(activeCriteria.bidDeadline && {
          BidDeadline: activeCriteria.bidDeadline.toISOString()
        }),
        ...(activeCriteria.notificationStatus !== 'All' && {
          NotificationStatus: activeCriteria.notificationStatus
        })
      });

      const response = (await httpRequest(
        'GET',
        `/bid_portal/bids/notifications?${queryParams}`
      )) as NotificationAPIResponse;

      if (response.status === 'success') {
        setNotifications(response.data.Data);
        setTotalCount(response.data.TotalRecords);
      } else {
        toast.error(response.message || 'Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to fetch notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and pagination changes
  useEffect(() => {
    fetchNotifications(page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchNotifications(0, rowsPerPage);
  };

  // Handle clear - Updated to clear contractNo instead of contractNos
  const handleClear = () => {
    const clearedCriteria: SearchCriteria = {
      titleEmailSubject: '',
      contractNo: '',
      statuses: [],
      types: [],
      categories: [],
      bidDeadline: null,
      notificationStatus: 'All'
    };

    setSearchCriteria(clearedCriteria);
    setPage(0);
    fetchNotifications(0, rowsPerPage, clearedCriteria); // Pass cleared criteria directly
  };

  // Pagination handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date and time
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return theme.palette.success.main;
      case 'failed':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 130,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Bid Notifications
        </Typography>
      </Box>

      {/* Error Alert */}
      {dropdownError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          {dropdownError}
        </Alert>
      )}

      {/* Search Filters Card */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Filters
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {loadingDropdowns ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {/* First row - 3 filters */}
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title / Email Subject"
                  variant="outlined"
                  value={searchCriteria.titleEmailSubject}
                  onChange={(e) =>
                    setSearchCriteria({
                      ...searchCriteria,
                      titleEmailSubject: e.target.value
                    })
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      height: 40
                    }
                  }}
                />
              </Grid>

              {/* Updated Contract No. dropdown - Single select */}
              <Grid item xs={12} sm={4} md={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Contract No.</InputLabel>
                  <Select
                    open={openContractSelect}
                    onOpen={() => setOpenContractSelect(true)}
                    onClose={() => setOpenContractSelect(false)}
                    label="Contract No."
                    multiple
                    value={
                      searchCriteria.contractNo
                        ? searchCriteria.contractNo.split(',')
                        : []
                    }
                    onChange={(e) => {
                      const selectedValues = e.target.value as string[]; // TypeScript assertion

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const isAllSelected =
                          searchCriteria.contractNo !== '' ||
                          (Array.isArray(searchCriteria.contractNo) &&
                            searchCriteria.contractNo.length ===
                              contractNumbers.length);

                        setSearchCriteria({
                          ...searchCriteria,
                          contractNo: isAllSelected
                            ? ''
                            : contractNumbers.join(',') // Join all contract numbers if "Select All"
                        });
                      } else {
                        setSearchCriteria({
                          ...searchCriteria,
                          contractNo: selectedValues.join(',') // Join selected contract numbers
                        });
                      }
                    }}
                    sx={{ borderRadius: 1.5, height: 40 }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                    renderValue={() => {
                      const selectedArray = searchCriteria.contractNo
                        ? searchCriteria.contractNo.split(',').filter(Boolean)
                        : [];
                      const totalSelected = selectedArray.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select contract numbers
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray.map((contractNo) => (
                              <Chip
                                key={contractNo}
                                label={contractNo}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  color: theme.palette.primary.main,
                                  fontWeight: 500,
                                  borderRadius: 1,
                                  flexShrink: 0,
                                  maxWidth: '120px',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    paddingLeft: '8px',
                                    paddingRight: '8px'
                                  }
                                }}
                              />
                            ))}
                          </Box>

                          {totalSelected > 1 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          searchCriteria.contractNo !== '' &&
                          searchCriteria.contractNo.split(',').filter(Boolean)
                            .length === contractNumbers.length
                        }
                        indeterminate={
                          searchCriteria.contractNo !== '' &&
                          searchCriteria.contractNo.split(',').filter(Boolean)
                            .length > 0 &&
                          searchCriteria.contractNo.split(',').filter(Boolean)
                            .length < contractNumbers.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Contract Number Options */}
                    {contractNumbers.map((contractNo, index) => (
                      <MenuItem
                        key={index}
                        value={contractNo}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            searchCriteria.contractNo
                              .split(',')
                              .indexOf(contractNo) > -1
                          }
                        />
                        {contractNo}
                      </MenuItem>
                    ))}

                    {/* Apply Button Menu Item */}
                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenContractSelect(false)} // Close the dropdown
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Status
                  </InputLabel>
                  <Select
                    open={openStatusSelect}
                    onOpen={() => setOpenStatusSelect(true)}
                    onClose={() => setOpenStatusSelect(false)}
                    multiple
                    value={searchCriteria.statuses}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allStatusIDs = statuses?.map(
                          (status) => status.StatusID
                        );
                        const isAllSelected =
                          searchCriteria.statuses?.length === statuses?.length;

                        // If all statuses are selected, deselect all; otherwise, select all
                        const finalStatuses = isAllSelected ? [] : allStatusIDs;

                        setSearchCriteria({
                          ...searchCriteria,
                          statuses: finalStatuses
                        });
                      } else {
                        // Ensure no invalid "Unknown" status is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            statuses.some((status) => status.StatusID === value)
                        );

                        setSearchCriteria({
                          ...searchCriteria,
                          statuses: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select statuses
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((statusId) => {
                              const statusName =
                                statuses.find((s) => s.StatusID === statusId)
                                  ?.Status || 'Unknown';
                              return (
                                <Chip
                                  key={statusId}
                                  label={statusName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          searchCriteria.statuses?.length === statuses?.length
                        }
                        indeterminate={
                          searchCriteria.statuses?.length > 0 &&
                          searchCriteria.statuses?.length < statuses?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Status Options */}
                    {statuses?.map((status) => (
                      <MenuItem
                        key={status.StatusID}
                        value={status.StatusID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            searchCriteria.statuses.indexOf(status.StatusID) >
                            -1
                          }
                        />
                        {status.Status}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenStatusSelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Second row - 3 filters */}
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Type
                  </InputLabel>
                  <Select
                    open={openTypeSelect}
                    onOpen={() => setOpenTypeSelect(true)}
                    onClose={() => setOpenTypeSelect(false)}
                    multiple
                    value={searchCriteria.types}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allTypeIDs = types?.map((type) => type.TypeID);
                        const isAllSelected =
                          searchCriteria.types?.length === types?.length;

                        // If all types are selected, deselect all; otherwise, select all
                        const finalTypes = isAllSelected ? [] : allTypeIDs;

                        setSearchCriteria({
                          ...searchCriteria,
                          types: finalTypes
                        });
                      } else {
                        // Ensure no invalid "Unknown" type is included
                        const validSelectedValues = selectedValues.filter(
                          (value) => types.some((type) => type.TypeID === value)
                        );

                        setSearchCriteria({
                          ...searchCriteria,
                          types: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select types
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((TypeID) => {
                              const TypeName =
                                types.find((t) => t.TypeID === TypeID)?.Type ||
                                'Unknown';
                              return (
                                <Chip
                                  key={TypeID}
                                  label={TypeName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={searchCriteria.types?.length === types?.length}
                        indeterminate={
                          searchCriteria.types?.length > 0 &&
                          searchCriteria.types?.length < types?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Type Options */}
                    {types?.map((type) => (
                      <MenuItem
                        key={type.TypeID}
                        value={type.TypeID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            searchCriteria.types.indexOf(type.TypeID) > -1
                          }
                        />
                        {type.Type}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenTypeSelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    open={openCategorySelect}
                    onOpen={() => setOpenCategorySelect(true)}
                    onClose={() => setOpenCategorySelect(false)}
                    multiple
                    value={searchCriteria.categories}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allCategoryIDs = categories?.map(
                          (cat) => cat.CategoryID
                        );
                        const isAllSelected =
                          searchCriteria.categories?.length ===
                          categories?.length;

                        // If all categories are selected, deselect all; otherwise, select all
                        const finalCategories = isAllSelected
                          ? []
                          : allCategoryIDs;

                        setSearchCriteria({
                          ...searchCriteria,
                          categories: finalCategories
                        });
                      } else {
                        // Ensure no invalid "Unknown" category is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            categories.some((cat) => cat.CategoryID === value)
                        );

                        setSearchCriteria({
                          ...searchCriteria,
                          categories: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select categories
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((CategoryID) => {
                              const categoryName =
                                categories.find(
                                  (c) => c.CategoryID === CategoryID
                                )?.CategoryName || 'Unknown';
                              return (
                                <Chip
                                  key={CategoryID}
                                  label={categoryName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          searchCriteria.categories?.length ===
                          categories?.length
                        }
                        indeterminate={
                          searchCriteria.categories?.length > 0 &&
                          searchCriteria.categories?.length < categories?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Category Options */}
                    {categories?.map((category) => (
                      <MenuItem
                        key={category.CategoryID}
                        value={category.CategoryID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            searchCriteria.categories.indexOf(
                              category.CategoryID
                            ) > -1
                          }
                        />
                        {category.CategoryName}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenCategorySelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* <Grid item xs={12} sm={4} md={4}>
                <DatePicker
                  label="Bid Deadline"
                  value={searchCriteria.bidDeadline}
                  onChange={(date) =>
                    setSearchCriteria({
                      ...searchCriteria,
                      bidDeadline: date
                    })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          height: 40
                        }
                      }
                    }
                  }}
                />
              </Grid> */}

              {/* Third row - Notification Status and Buttons in same row */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  <FormControl
                    component="fieldset"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        marginRight: 2
                      }}
                    >
                      Notification Status:
                    </Typography>
                    <RadioGroup
                      row
                      value={searchCriteria.notificationStatus}
                      onChange={(e) =>
                        setSearchCriteria({
                          ...searchCriteria,
                          notificationStatus: e.target.value
                        })
                      }
                      sx={{ display: 'flex', gap: 2, flexDirection: 'row' }}
                    >
                      <FormControlLabel
                        value="Delivered"
                        control={<Radio />}
                        label="Delivered"
                      />
                      <FormControlLabel
                        value="Failed"
                        control={<Radio />}
                        label="Failed"
                      />
                      <FormControlLabel
                        value="Pending"
                        control={<Radio />}
                        label="Pending"
                      />
                      <FormControlLabel
                        value="All"
                        control={<Radio />}
                        label="All"
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Search and Clear buttons on the right */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.8,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleClear}
                      startIcon={<ClearAll />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.8,
                        fontWeight: 'bold',
                        borderWidth: '1px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderWidth: '1px',
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.04
                          )
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            bgcolor: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Total Results: {totalCount}
              </Typography>
            )}
            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<Print />}
              sx={{
                borderRadius: 1.5,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              Print
            </Button> */}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    py: 1.8
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Email Subject</TableCell>
                    <TableCell>Email/Fax</TableCell>
                    <TableCell>Sent On</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No notifications found. Try adjusting your search
                            criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    notifications?.map((notification) => (
                      <TableRow
                        key={notification.NotificationID}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.03)
                          },
                          transition: 'background-color 0.2s ease',
                          '&:nth-of-type(even)': {
                            bgcolor: alpha(
                              theme.palette.background.default,
                              0.4
                            )
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {notification.EmailSubject}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {notification.EmailID}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {formatDateTime(notification.SentOn)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={notification.EmailStatus}
                            size="small"
                            sx={{
                              bgcolor: alpha(
                                getStatusColor(notification.EmailStatus),
                                0.1
                              ),
                              color: getStatusColor(notification.EmailStatus),
                              fontWeight: 'bold',
                              borderRadius: 1
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      fontWeight: 500
                    },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    '&:focus': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BidNotifications;
